import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { JobFile, LoadKeyDetails } from '@core/models/job-file/job-file.model';
import { JobFileService } from '@core/services/job-file.service';
import { AuthState } from '@core/store/auth/auth.state';
import { API } from '@configs/api.config';
import { JobFileContainer } from '@core/models/job-file/job-file-container.model';
import { VehicleDetails } from '@shared/models/vehicle-details.model';
import { JobFileServiceModel } from '@core/models/job-file/job-file-service.model';
import { BillingInfo } from '@core/models/job-file/billing-info.model';
import { JobFileLeg } from '@core/models/job-file/job-file-leg.model';
import { ServiceSequenceTemplate } from '../../wizard/models/service-sequence-template.model';
import { AddContainer } from '../../wizard/models/add-container.model';
import { InitLeg } from '../../wizard/models/init-leg.model';
import { VehicleCategory } from '@shared/models/vehicle-category.model';
import { PartnerOrganization, PartnerOrganizationSearch } from '../models/partner-organization.model';
import { PartnerVehicle } from '../models/partner-vehicle.model';
import { Pageable } from '@shared/models/pageable.model';
import { map, switchMap } from 'rxjs/operators';
import { LEG_PATHS_HEADER, LEG_PATHS_IDS_ONLY, RoutePolylineService } from '@core/services/route-polyline.service';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';
import { ContainerTypeAndSize } from '@control-tower/models/jpm/jpm-container-type-and-size.model';
import { InitContainerViewModel } from '../../wizard/models/init-container-view.model';
import { CloudRunSyncOptimizerToggleService } from '@shared/services/cloud-run-sync-optimizer-toggle.service';

export interface BatchSaveServiceItem {
  // Required only when service.id is null (an add) - the position of this row's predecessor in the
  // container as the server holds it, same convention as the single-row add endpoint's index.
  index?: number;
  service: JobFileServiceModel;
}

export interface BatchSaveServiceResult {
  service?: JobFileServiceModel;
  status: 'COMPLETED' | 'FAILED';
  issues?: string[];
}

export interface BatchSaveServicesResponse {
  items: BatchSaveServiceResult[];
  // Full final container state (every service, not just the ones in the request) plus the
  // container-level fields the map/legs display depends on - lets the caller patch its container
  // state locally instead of a separate GetContainer round trip for the same data.
  services: JobFileServiceModel[];
  legs: InitLeg[];
  billableEstimatedTime: number;
  distanceBetweenStartAndEnd: number;
  useExpressWay: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LoadManagementService {

  constructor(
    private readonly jobFileService: JobFileService,
    private readonly httpClient: HttpClient,
    private readonly store: Store,
    private readonly cloudRunSyncToggle: CloudRunSyncOptimizerToggleService,
    private readonly routePolylineService: RoutePolylineService
  ) {}

  /**
   * The job file with leg route ids only (`encodedPathId`, no `encodedPath`): every load's routes were
   * most of the response, and the screen draws one load at a time. ServiceRouteMapComponent fetches the
   * routes of the load it shows; legs sent back with only their id keep their routes.
   */
  getJobFile(orgId: string, jobRefId: string): Observable<JobFile> {
    const url = API.jobFile.getJobFileByOrgIdAndJobRefId.format([orgId, jobRefId]);
    return this.httpClient.get<JobFile>(url, { headers: { [LEG_PATHS_HEADER]: LEG_PATHS_IDS_ONLY } });
  }

  addNewContainer(orgId: string, jobRefId: string, vehicleDetails: VehicleDetails[]): Observable<JobFileContainer[]> {
    const url = API.jobFile.addNewContainerForBookingEngine.format([orgId, jobRefId]);
    return this.httpClient.post<JobFileContainer[]>(url, vehicleDetails);
  }

  updateService(orgId: string, jobRefId: string, containerId: string, service: JobFileServiceModel): Observable<JobFileServiceModel> {
    const url = API.jobFile.updateService.format([orgId, jobRefId, containerId]);
    return this.httpClient.post<JobFileServiceModel>(url, service);
  }

  setBilling(orgId: string, jobRefId: string, containerId: string, serviceId: string, billingInfo: BillingInfo): Observable<JobFileServiceModel> {
    const url = API.jobFile.setBillingStartAndEnd.format([orgId, jobRefId, containerId, serviceId]);
    return this.httpClient.post<JobFileServiceModel>(url, billingInfo);
  }

  setLegFreezer(orgId: string, jobRefId: string, containerId: string, legIndex: number, useFreezer: boolean): Observable<JobFileLeg> {
    const url = API.jobFile.setLegFreezer.format([orgId, jobRefId, containerId, legIndex.toString()]);
    return this.httpClient.post<JobFileLeg>(url, { useFreezer });
  }

  addService(orgId: string, jobRefId: string, containerId: string, index: number, service: JobFileServiceModel): Observable<JobFileServiceModel> {
    const url = API.jobFile.addService.format([orgId, jobRefId, containerId, index.toString()]);
    return this.httpClient.post<JobFileServiceModel>(url, service);
  }

  /**
   * `useCache: true` sends an empty items[] body - the backend then loads items from
   * service_staging_cache by sessionId instead, because the caller already proved that row is a
   * byte-identical copy of what it would otherwise upload (see
   * RouteSetupDialogComponent.processBatchSave's hash check). The rest of the save - mutation
   * validation, route calc, the Mongo write - is identical either way; only where items[] comes
   * from changes.
   */
  batchSaveServices(
    orgId: string, jobRefId: string, containerId: string, items: BatchSaveServiceItem[], sessionId?: string,
    useCache = false
  ): Observable<BatchSaveServicesResponse> {
    const url = API.jobFile.batchSaveServices.format([orgId, jobRefId, containerId]);
    // The backend's only signal is "items empty + sessionId resolves to a cache row" - no
    // separate useCache flag needed on the wire, it's purely a local branch on what body to send.
    const params = sessionId ? new HttpParams().set('sessionId', sessionId) : undefined;
    return this.httpClient
      .post<BatchSaveServicesResponse>(url, useCache ? [] : items, { headers: { [LEG_PATHS_HEADER]: LEG_PATHS_IDS_ONLY }, params })
      .pipe(switchMap(response => this.routePolylineService.fillLegPaths(response, response?.legs)));
  }

  /**
   * Stages a route-setup-dialog "+" add/update for early per-item validation and crash recovery -
   * see ServiceStagingCache's backend doc comment. Never touches route calc or the real job file;
   * Confirm (batchSaveServices) is unaffected by whatever this does or doesn't return.
   */
  stageServices(
    orgId: string, jobRefId: string, containerId: string, sessionId: string, items: BatchSaveServiceItem[]
  ): Observable<BatchSaveServiceResult[]> {
    const url = API.jobFile.stageServiceBatch.format([orgId, jobRefId, containerId]);
    const params = new HttpParams().set('sessionId', sessionId);
    return this.httpClient.post<BatchSaveServiceResult[]>(url, items, { params });
  }

  /** Idempotent - discarding an id that's missing or already expired is not an error. */
  discardStaged(orgId: string, jobRefId: string, containerId: string, sessionId: string): Observable<void> {
    const url = API.jobFile.discardStagedServiceBatch.format([orgId, jobRefId, containerId, sessionId]);
    return this.httpClient.delete<void>(url);
  }

  removeService(orgId: string, jobRefId: string, containerId: string, serviceId: string): Observable<JobFile> {
    const url = API.jobFile.removeService.format([orgId, jobRefId, containerId, serviceId]);
    // Removing a service re-optimizes DISTRIBUTION jobs server-side (ORToolService) — carry the sync toggle.
    const params = new HttpParams().set('useCloudRunSync', (!!this.cloudRunSyncToggle.useCloudRunSync).toString());
    return this.httpClient.get<JobFile>(url, { params });
  }

  generateDryServiceByActivityId(
    activityId: string,
    billingActivated: boolean,
    billingIndicator: string | null,
    jobRefId: string,
    containerId: string,
    serviceId?: string
  ): Observable<JobFileServiceModel> {
    const url = API.jobFile.generateDryServiceByActivityId.format([
      activityId,
      billingActivated.toString(),
      billingIndicator || 'null',
      jobRefId,
      containerId
    ]);
    
    let params = new HttpParams();
    // Always include serviceId - pass empty string for new services
    if (serviceId && !serviceId.startsWith('new-service-')) {
      params = params.set('serviceId', serviceId);
    } else {
      params = params.set('serviceId', '');
    }
    
    return this.httpClient.post<JobFileServiceModel>(url, null, { params });
  }

  getSavedRoutes(jobRefId: string, orgId: string, operationId: string, searchKey: string): Observable<ServiceSequenceTemplate[]> {
    const url = API.jobFile.getContainerTemplatesByOperation.format([orgId, jobRefId, operationId, searchKey]);
    return this.httpClient.get<ServiceSequenceTemplate[]>(url);
  }

  saveServiceSequenceTemplate(template: ServiceSequenceTemplate): Observable<ServiceSequenceTemplate> {
    const url = API.jobFile.saveServiceSequenceTemplate;
    return this.httpClient.post<ServiceSequenceTemplate>(url, template);
  }

  updateServiceSequenceTemplate(template: ServiceSequenceTemplate): Observable<ServiceSequenceTemplate> {
    const url = API.jobFile.updateServiceSequenceTemplate;
    return this.httpClient.patch<ServiceSequenceTemplate>(url, template);
  }

  applyServiceSequenceTemplate(orgId: string, jobRefId: string, containerId: string, templateId: string): Observable<JobFile> {
    const url = API.jobFile.applyServiceSequenceTemplate.format([orgId, jobRefId, containerId, templateId]);
    return this.httpClient.post<JobFile>(url, null);
  }

  removeServiceSequenceTemplate(templateId: string): Observable<any> {
    const url = API.jobFile.removeTemplate.format([templateId]);
    return this.httpClient.delete<any>(url);
  }

  removeContainer(orgId: string, jobRefId: string, containerId: string, ordersSplitted?: boolean): Observable<string> {
    let url = API.jobFile.removeContainer.format([orgId, jobRefId, containerId]);
    if (ordersSplitted) {
      url += '?ordersSplitted=true';
    }
    return this.httpClient.post<string>(url, null);
  }

  getContainer(containerId: string, jobRefId: string, orgId: string): Observable<AddContainer> {
    const url = API.jobFile.getContainer.format([orgId, jobRefId, containerId]);
    return this.httpClient.get<AddContainer>(url).pipe(
      switchMap(container => this.routePolylineService.fillLegPaths(container, container?.legs))
    );
  }

  duplicateContainer(orgId: string, jobRefId: string, containerId: string, newOrder?: boolean, noOfContainers?: number): Observable<number> {
    const url = API.jobFile.duplicateContainer.format([orgId, jobRefId, containerId]);
    let params = new HttpParams();
    params = params.set('newOrder', (newOrder) ? String(newOrder) : String(false));
    params = params.set('noOfContainers', (noOfContainers) ? String(noOfContainers) : '');
    return this.httpClient.post<number>(url, null, { params });
  }

  getVehicleCategories(isStandard?: boolean): Observable<VehicleCategory[]> {
    let params = new HttpParams();
    if (isStandard !== undefined) {
      params = params.append('isStandard', isStandard ? isStandard.toString() : 'false');
    }
    const url = API.organization.getVehicleCategories;
    return this.httpClient.get<VehicleCategory[]>(url, { params });
  }


  getContainerTypeAndSize(): Observable<ContainerTypeAndSize[]> {
    const url = API.assets.getContainerTypesAndSizes;
    return this.httpClient.get<ContainerTypeAndSize[]>(url);
  }

 
  transformContainerTypesToVehicleCategories(containerTypes: ContainerTypeAndSize[]): VehicleCategory[] {
    return containerTypes.map((container, index) => ({
      id: `container-${index}`,
      categoryName: container.label,
      maxLoadVolume: 0, 
      maxLoadWeight: Number(container.maxPayloadKG) || 0,
      costPerKm: 0 
    }));
  }

  /**
   * Get paginated partner organizations
   */
  getPartnerOrganizations(searchText: string, pageNum: number, pageSize: number): Observable<Pageable<PartnerOrganization>> {
    let params = new HttpParams();
    const url = API.organization.getPaginatedPartnerOrganizationsByOperations;
    
    if (searchText != null && searchText.trim() !== '') {
      params = params.append('searchKey', searchText);
    }

    if (pageNum != null) {
      params = params.append('pageNum', String(pageNum));
    }

    if (pageSize != null) {
      params = params.append('pageSize', String(pageSize));
    }

    return this.httpClient.get<Pageable<any>>(url, { params }).pipe(
      map(response => ({
        ...response,
        content: response.content?.map((org: any) => this.mapToPartnerOrganization(org)) || []
      }))
    );
  }

  /**
   * Search partner organizations (lightweight)
   */
  searchPartnerOrganizations(searchText: string, pageNum: number, pageSize: number): Observable<Pageable<PartnerOrganizationSearch>> {
    let params = new HttpParams();
    const url = API.organization.searchPartnerOrganizationsByOperations;
    
    if (searchText != null && searchText.trim() !== '') {
      params = params.append('searchKey', searchText);
    }

    if (pageNum != null) {
      params = params.append('pageNum', String(pageNum));
    }

    if (pageSize != null) {
      params = params.append('pageSize', String(pageSize));
    }

    return this.httpClient.get<Pageable<any>>(url, { params }).pipe(
      map(response => ({
        ...response,
        content: response.content?.map((org: any) => this.mapToPartnerOrganizationSearch(org)) || []
      }))
    );
  }

  /**
   * Get vehicles by organization ID
   */
  getVehiclesByOrganization(orgId: string): Observable<PartnerVehicle[]> {
    const url = API.organization.getVehiclesByManagingOrg.format([orgId]);
    return this.httpClient.get<any[]>(url).pipe(
      map(vehicles => vehicles?.map(vehicle => this.mapToPartnerVehicle(vehicle)) || [])
    );
  }

  /**
   * Map API organization response to PartnerOrganization model
   */
  private mapToPartnerOrganization(org: any): PartnerOrganization {
    return {
      id: org.id,
      orgName: org.orgName,
      shortName: org.shortName,
      orgType: org.orgType,
      logo: org.logo,
      phoneNumbers: org.phoneNumbers,
      companyWebSite: org.companyWebSite
    };
  }

  /**
   * Map API organization search response to PartnerOrganizationSearch model
   */
  private mapToPartnerOrganizationSearch(org: any): PartnerOrganizationSearch {
    return {
      id: org.id,
      orgName: org.orgName
    };
  }

  /**
   * Map API vehicle response to PartnerVehicle model
   */
  private mapToPartnerVehicle(vehicle: any): PartnerVehicle {
    return {
      id: vehicle.id,
      registrationNumber: vehicle.registrationNumber,
      vehicleCategoryName: vehicle.vehicleCategory?.categoryName,
      vehicleType: vehicle.type,
      capacity: vehicle.vehicleCategory?.capacity,
      weight: vehicle.vehicleCategory?.weight,
      managedByOrg: vehicle.managedByOrg,
      ownedByOrg: vehicle.ownedByOrg
    };
  }

  /**
   * Get digital twin vehicles from backend
   * Used when expanding organizations to show available vehicles
   */
  getDigitalTwinVehicles(payload: {
    orgId: string;
    operationId: string;
    vehicleCategory: string;
    supplierIds: string[];
    searchText: string;
    type: string;
  }, page: number = 0, size: number = 20): Observable<any> {
    const url = API.organization.getDigitalTwinVehicles.format([]);
    let params = new HttpParams();
    params = params.set('page', String(page));
    params = params.set('size', String(size));
    return this.httpClient.post<any>(url, payload, { params });
  }

  /**
   * Get digital twin vehicles grouped by organization
   * Returns vehicles organized by their managing organization
   */
  getDigitalTwinVehiclesByOrg(payload: {
    orgId: string;
    operationId?: string;
    vehicleCategory?: string;
    searchText?: string;
    type?: string;
  }): Observable<any> {
    const url = API.organization.getDigitalTwinVehiclesByOrg.format([]);
    return this.httpClient.post<any>(url, payload);
  }

  /**
   * Slim variant of getDigitalTwinVehiclesByOrg: same vehicles, only the fields the dialog renders,
   * and each group carries its orgName so no separate organization lookup is needed.
   */
  getDigitalTwinVehiclesByOrgSlim(payload: {
    orgId: string;
    operationId?: string;
    vehicleCategory?: string;
    searchText?: string;
    type?: string;
  }): Observable<any> {
    const url = API.organization.getDigitalTwinVehiclesByOrgSlim.format([]);
    return this.httpClient.post<any>(url, payload);
  }

  /**
   * Get organization details by ID
   */
  getOrganizationById(orgId: string): Observable<any> {
    const url = API.organization.getOrganization.format([orgId]);
    return this.httpClient.get<any>(url);
  }

  /**
   * Get multiple organizations by their IDs
   */
  getOrganizationsByIds(orgIds: string[]): Observable<any[]> {
    const url = API.organization.findOrgByIds;
    return this.httpClient.post<any[]>(url, orgIds);
  }

  inviteCollaborators(collaboratorsInvitationModel: CollaboratorsInvitationModel): Observable<JobFile> {
    const url = API.jobFile.inviteCollaborators;
    return this.httpClient.post<JobFile>(url, collaboratorsInvitationModel);
  }

  optimizeContainers(orgId: string, jobRefId: string, containerIds: string[], useCloudRunSync: boolean = false): Observable<any> {
    // ?useCloudRunSync=true routes the sync optimize through the gateway -> Cloud Run; false = GKE.
    const url = API.jobFile.optimizeContainers.format([orgId, jobRefId]) + `?useCloudRunSync=${!!useCloudRunSync}`;
    return this.httpClient.post(url, containerIds);
  }

  updateContainerFcl(
    orgId: string,
    jobRefId: string,
    containerId: string,
    payload: {
      containerId: string;
      containerNumber?: string;
      selectionMethod: string;
      typeAndSize: ContainerTypeAndSize;
    }
  ): Observable<any> {
    const url = API.jobFile.updateContainerFcl.format([orgId, jobRefId, containerId]);
    return this.httpClient.post<any>(url, payload);
  }

  addVehicleCategoryFieldsToContainer(orgId: string, jobRefId: string, containerId: string, vehicleCategoryId: string): Observable<any> {
    const url = API.jobFile.addVehicleCategoryFieldsToContainer.format([orgId, jobRefId, containerId])
      + `?vehicleCategoryId=${vehicleCategoryId}`;
    return this.httpClient.post<any>(url, {});
  }

  /** Rebuilds the container's full vehicleData (category, capacity, costPerKM) for a standard vehicle. */
  addVehicleDataToContainer(orgId: string, jobRefId: string, containerId: string, vehicleCategoryId: string): Observable<any> {
    const url = API.jobFile.addVehicleDataToContainer.format([orgId, jobRefId, containerId])
      + `?vehicleCategoryId=${vehicleCategoryId}`;
    return this.httpClient.post<any>(url, {});
  }

  updateCustomsChannel(orgId: string, jobRefId: string, containerId: string, customsChannel: string): Observable<any> {
    const url = API.jobFile.updateCustomsChannel.format([orgId, jobRefId, containerId, customsChannel]);
    return this.httpClient.post<any>(url, {});
  }

  updateLoadKeyDetails(orgId: string, jobRefId: string, containerId: string, loadKeyDetails: LoadKeyDetails): Observable<LoadKeyDetails> {
    const url = API.jobFile.updateLoadKeyDetails.format([orgId, jobRefId, containerId]);
    return this.httpClient.post<LoadKeyDetails>(url, loadKeyDetails);
  }

  updateContainer(orgId: string, jobRefId: string, addContainer: AddContainer): Observable<string> {
    const url = API.jobFile.updateContainer.format([orgId, jobRefId, addContainer.containerId]);
    return this.httpClient.post<string>(url, addContainer);
  }

  /**
   * Same endpoint as wizard waypoint save; used by load-management route map only.
   * Responds with the saved container, whose legs carry the distance / travelingTime
   * recalculated through the new waypoints.
   */
  updateContainerRouteWaypoints(orgId: string, jobRefId: string, addContainer: AddContainer): Observable<AddContainer> {
    const url = API.jobFile.updateContainerWhenAddWayPoint.format([orgId, jobRefId]);
    return this.httpClient.post<AddContainer>(url, addContainer).pipe(
      switchMap(container => this.routePolylineService.fillLegPaths(container, container?.legs))
    );
  }

  findInitContainersByContainerIds(orgId: string, jobRefId: string, containerIds: string[]): Observable<JobFileContainer[]> {
    const url = API.jobFile.findInitContainersByContainerIds.format([orgId, jobRefId]);
    return this.httpClient.post<JobFileContainer[]>(url, containerIds);
  }

  /** Load-master status per load id (ms-report projects containerId + status only). */
  getLoadMasterStatuses(orgId: string, loadIds: string[]): Observable<Record<string, string>> {
    const url = API.report.getLoadStatusesByLoadIds.format([orgId]);
    return this.httpClient.post<Record<string, string>>(url, { loadIds });
  }
}
